/* Gallery API deliberately matches the existing AYAQ variant picker. */
if (!customElements.get('ayaq-pdp-gallery')) {
  customElements.define(
    'ayaq-pdp-gallery',
    class extends HTMLElement {
      connectedCallback() {
        this.scroller = this.querySelector('[data-pdp-slides]');
        if (!this.scroller || this.controller) return;
        this.controller = new window.AbortController();
        const { signal } = this.controller;
        this.slides = Array.from(this.scroller.children);
        this.rail = this.querySelector('[data-pdp-thumbnails]');
        this.railItems ||= Array.from(this.rail.children);
        this.thumbnails = Array.from(
          this.querySelectorAll('[data-pdp-target]'),
        );
        this.addEventListener(
          'click',
          (event) => {
            const thumbnail = event.target.closest('[data-pdp-target]');
            const arrow = event.target.closest('[data-pdp-rail-direction]');
            const poster = event.target.closest(
              'deferred-media > .deferred-media__poster',
            );
            if (poster) this.playVideo(poster.closest('deferred-media'));
            if (thumbnail) this.goTo(thumbnail.dataset.pdpTarget, true);
            if (arrow) {
              this.navigateRail(Number(arrow.dataset.pdpRailDirection));
            }
          },
          { signal },
        );
        this.scroller.addEventListener(
          'scroll',
          () => {
            if (this.frame) return;
            this.frame = requestAnimationFrame(() => {
              this.frame = null;
              const left = this.scroller.getBoundingClientRect().left;
              const closest = this.visibleSlides().reduce((best, slide) => {
                const distance = Math.abs(
                  slide.getBoundingClientRect().left - left,
                );
                return !best || distance < best.distance
                  ? { slide, distance }
                  : best;
              }, null);
              if (closest) this.updateCurrent(closest.slide);
            });
          },
          { passive: true, signal },
        );
        this.resizeObserver = new ResizeObserver(() => {
          const header = document.querySelector('.ayaq-header-container');
          const height = header ? header.getBoundingClientRect().height : 0;
          this.closest('.ayaq-pdp')?.style.setProperty(
            '--ayaq-pdp-header',
            `${height}px`,
          );
          this.updateRailArrows();
        });
        const header = document.querySelector('.ayaq-header-container');
        if (header) this.resizeObserver.observe(header);
        this.resizeObserver.observe(this.rail);
        this.resizeObserver.observe(this.scroller);
        this.updateCurrent(
          this.slides.find((slide) => slide.classList.contains('is-active')) ||
            this.visibleSlides()[0],
        );
        this.classList.add('ayaq-pdp__gallery--ready');
      }

      disconnectedCallback() {
        this.controller?.abort();
        this.controller = null;
        this.resizeObserver?.disconnect();
        if (this.frame) cancelAnimationFrame(this.frame);
        this.frame = null;
        this.currentMediaId = null;
      }

      playVideo(player) {
        // The shared deferred component loads the player; this click starts it.
        if (!player.hasAttribute('loaded')) player.loadContent(false);
        const video = player.querySelector('video');
        if (video) {
          video.play().catch(() => {
            // Keep the native controls available if playback cannot start.
            video.controls = true;
          });
        }
        const frame = player.querySelector('iframe');
        if (frame) {
          const source = new window.URL(frame.src);
          source.searchParams.set('autoplay', '1');
          frame.src = source.href;
        }
      }

      visibleSlides() {
        return this.slides.filter((slide) => !slide.hidden);
      }

      visibleThumbnails() {
        return Array.from(this.rail.children).filter((item) => !item.hidden);
      }

      updateRailArrows() {
        const items = this.visibleThumbnails();
        this.querySelectorAll('[data-pdp-rail-direction]').forEach((arrow) => {
          arrow.disabled = items.length < 2;
        });
        const bounds = this.rail.getBoundingClientRect();
        items.forEach((item) => {
          const box = item.getBoundingClientRect();
          item.inert = box.top >= bounds.bottom || box.bottom <= bounds.top;
        });
      }

      navigateRail(direction) {
        const items = this.visibleThumbnails();
        if (items.length < 2) return;
        const selectedSlot = Math.max(
          0,
          items.findIndex((item) =>
            item.querySelector('[aria-current="true"]'),
          ),
        );
        // Rotate the media, preserving the selected visual slot. No scrolling.
        if (direction > 0) {
          items.at(-1).after(items[0]);
        } else {
          items[0].before(items.at(-1));
        }
        const target =
          this.visibleThumbnails()[selectedSlot].querySelector(
            '[data-pdp-target]',
          );
        this.goTo(target.dataset.pdpTarget, false);
      }

      setActiveMedia(mediaId) {
        const target = this.slides.find(
          (slide) => slide.dataset.mediaId === mediaId,
        );
        if (!target) return;
        const color = target.dataset.color;
        // A variant starts with its own ordered media, independent of previous rotations.
        this.rail.append(...this.railItems);
        this.querySelectorAll('[data-color]').forEach((item) => {
          item.hidden = Boolean(
            item.dataset.color && item.dataset.color !== color,
          );
        });
        this.goTo(mediaId, false);
      }

      goTo(mediaId, animate) {
        const target = this.visibleSlides().find(
          (slide) => slide.dataset.mediaId === mediaId,
        );
        if (!target) return;
        const reduceMotion = window.matchMedia(
          '(prefers-reduced-motion: reduce)',
        ).matches;
        const offset =
          target.getBoundingClientRect().left -
          this.scroller.getBoundingClientRect().left;
        this.scroller.scrollTo({
          left: this.scroller.scrollLeft + offset,
          behavior:
            animate &&
            !reduceMotion &&
            window.matchMedia('(max-width: 989px)').matches
              ? 'smooth'
              : 'instant',
        });
        this.updateCurrent(target);
      }

      updateCurrent(target) {
        if (!target) return;
        const visible = this.visibleSlides();
        if (
          this.currentMediaId === target.dataset.mediaId &&
          this.currentCount === visible.length
        ) {
          return;
        }
        this.currentMediaId = target.dataset.mediaId;
        this.currentCount = visible.length;
        const index = visible.indexOf(target);
        const image = target.querySelector('img');
        if (image?.getAttribute('width') && image?.getAttribute('height')) {
          this.closest('.ayaq-pdp').style.setProperty(
            '--pdp-media-ratio',
            Number(image.getAttribute('width')) /
              Number(image.getAttribute('height')),
          );
        }
        this.slides.forEach((slide) => {
          const active = slide === target;
          slide.classList.toggle('is-active', active);
          slide.inert = !active;
          if (!active) {
            slide.querySelectorAll('video').forEach((video) => video.pause());
            slide.querySelectorAll('iframe').forEach((frame) => {
              const message = frame.classList.contains('js-youtube')
                ? { event: 'command', func: 'pauseVideo', args: [] }
                : { method: 'pause' };
              frame.contentWindow?.postMessage(
                JSON.stringify(message),
                new window.URL(frame.src).origin,
              );
            });
          }
        });
        this.thumbnails.forEach((button) => {
          if (button.dataset.pdpTarget === target.dataset.mediaId) {
            button.setAttribute('aria-current', 'true');
          } else button.removeAttribute('aria-current');
        });
        this.querySelector('[data-pdp-current]').textContent = String(
          index + 1,
        );
        this.querySelector('[data-pdp-total]').textContent = String(
          visible.length,
        );
        this.querySelector('[data-pdp-pagination]').hidden = visible.length < 2;
        this.updateRailArrows();
      }
    },
  );
}
